import { routes } from "@constants/routes";
import {
  ArrowRightIcon,
  BuildingIcon,
  FlagIcon,
  handleApiRequest,
  ImageInput,
  Typography,
  useFetchFile,
  useFileHandler,
  useMobile,
  UserTypeEnum,
} from "@cpj-ai/panel-npm-package";
import {
  UpdateAvatarMutation,
  UpdateAvatarMutationVariables,
  UploadType,
  useUpdateAvatarMutation,
} from "@graphql/generated";
import { Avatar, Button, Card, Spinner } from "@heroui/react";
import { useProfile } from "@hooks/useProfile";
import { UploadResumeFile } from "@modules/Resume/components/UploadResumeFile";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

interface IUserInformationProps {
  mode: "VERTICAL" | "HORIZONTAL";
  showUploadedResumeLink?: boolean;
  showCompletingResumeBtn?: boolean;
  showUploadAvatarBtn?: boolean;
  collapsibleInMobile?: boolean;
  defaultCollapseValue?: boolean;
  classes?: {
    avatar?: string;
    base?: string;
  };
}

const UserInformation = ({
  mode,
  classes,
  showUploadedResumeLink = false,
  showCompletingResumeBtn = false,
  showUploadAvatarBtn = false,
  collapsibleInMobile = false,
  defaultCollapseValue = false,
}: IUserInformationProps) => {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapseValue);
  const isMobile = useMobile();
  const { profile, fetchProfile, loading: getProfileIsLoading } = useProfile();
  const { name, email, avatar, country, city, userResumeFile } = profile;
  const [updateAvatar, { loading: uploadImageIsLoading }] = useUpdateAvatarMutation();
  const { fetchFile: fetchUserResumeBlob, loading: loadingUserResumeBlob } = useFetchFile({
    urlPath: userResumeFile,
    downloadMode: true,
    userType: UserTypeEnum.USER,
  });
  const { data: avatarBlob } = useFetchFile({ urlPath: avatar, userType: UserTypeEnum.USER });

  const field = profile.workexperiences?.[0]?.jobTitle;

  const navigateToResume = useCallback(() => {
    navigate(routes.resume.main);
  }, [navigate]);

  const { handleUpload, isLoading: uploadIsLoading } = useFileHandler({
    uploadType: UploadType.AVATAR,
    userType: UserTypeEnum.USER,
  });
  const handleUploadAvatar = useCallback(
    async (file: File | null) => {
      if (!file) return toast.error("No file selected");

      const fileId = await handleUpload(file);

      if (!fileId) {
        toast.error("Failed to upload avatar");
        return null;
      }

      await handleApiRequest<UpdateAvatarMutation, UpdateAvatarMutationVariables>({
        apiFunction: updateAvatar,
        variables: {
          avatar: String(fileId),
        },
        componentName: "ProfileAvatar",
        actionName: "updateAvatar",
        successMessage: "Profile Avatar Updated",
        fallbackErrorMessage: "Failed to update profile avatar",
        extraErrorContext: {
          fileId,
        },
        onSuccess: () => {
          fetchProfile();
        },
      });
      return fileId;
    },
    [fetchProfile, handleUpload, updateAvatar],
  );

  useEffect(() => {
    if (!isMobile) {
      setIsCollapsed(false);
    }
  }, [isMobile]);

  return (
    <Card classNames={{ base: classes?.base }}>
      {!getProfileIsLoading ? (
        <div
          className={`p-3 md:p-5 ${mode === "VERTICAL" ? "" : "lg:flex lg:justify-between lg:gap-6 xl:gap-0 xl:grid xl:grid-cols-3"}`}
        >
          <div className={`flex items-center flex-1 relative ${mode === "HORIZONTAL" ? "gap-4" : "gap-3"}`}>
            {collapsibleInMobile && (
              <Button
                className="absolute top-0 right-0 p-1 min-w-0 w-fit h-fit md:hidden"
                isIconOnly
                radius="full"
                onPress={() => setIsCollapsed(!isCollapsed)}
              >
                {isCollapsed ? (
                  <ArrowRightIcon stroke="black" width={20} className="-rotate-90" />
                ) : (
                  <ArrowRightIcon stroke="black" width={20} className="rotate-90" />
                )}
              </Button>
            )}
            <Avatar
              radius="md"
              src={avatarBlob}
              isDisabled={avatarBlob ? false : true}
              classNames={{ img: "opacity-100" }}
              className={`${
                mode === "HORIZONTAL"
                  ? "w-[70px] min-w-[70px] h-20 md:min-w-20 md:w-20 md:h-[90px]"
                  : "w-10 min-w-10 h-12"
              } ${classes?.avatar}`}
            />

            <div className="flex flex-col justify-between w-fit gap-2">
              <div className="flex flex-col gap-1">
                <Typography className="text-sm md:text-lg">{name}</Typography>
                <p className="text-[10px] md:text-sm text-[#A1A1AA]">{email}</p>
              </div>
              {showUploadAvatarBtn && (
                <div className="flex">
                  <ImageInput
                    layout="button"
                    loading={uploadImageIsLoading || uploadIsLoading}
                    onChange={handleUploadAvatar}
                  />
                </div>
              )}
            </div>
          </div>
          <div
            className={`flex w-full justify-between ${
              mode === "HORIZONTAL"
                ? "hidden md:flex flex-col md:flex-row items-end md:divide-x-1 gap-4 md:gap-0 md:pt-0 "
                : "flex-col divide-y-1 gap-4 pt-3"
            } transition-all duration-300 ease-in-out origin-top ${
              isCollapsed ? "scale-y-0 h-0 opacity-0 overflow-hidden" : "scale-y-100 opacity-100 pt-3 col-span-2 "
            }`}
          >
            {mode !== "HORIZONTAL" && (
              <div className="w-full lg:flex-1">
                <div className={`flex flex-col gap-2 lg:gap-3 justify-end md:flex-1`}>
                  {(city || country) && (
                    <div className="flex items-center gap-2">
                      <FlagIcon className="w-5 h-5 lg:w-6 lg:h-6" />
                      <Typography className="text-xs lg:text-sm">{`${city ?? "City"} , ${country ?? "Country"}`}</Typography>
                    </div>
                  )}
                  {field && (
                    <div className="flex items-center gap-2">
                      <BuildingIcon className="w-5 h-5 lg:w-6 lg:h-6" />
                      <Typography className="text-xs lg:text-sm">{field}</Typography>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className={mode === "VERTICAL" ? "pt-3" : "w-full lg:flex-[1.5] xl:flex-1 hidden"}>
              {showUploadedResumeLink && profile.userResumeFile && (
                <div className="flex justify-between mt-5 text-xs">
                  <p>Resume uploaded:</p>
                  <div onClick={fetchUserResumeBlob} className="text-blue-500 cursor-pointer">
                    {!loadingUserResumeBlob ? (
                      userResumeFile?.split("/")?.at(-1)
                    ) : (
                      <Spinner size="sm" className="ms-4" />
                    )}
                  </div>
                </div>
              )}
              {showCompletingResumeBtn && <UploadResumeFile />}
              {showCompletingResumeBtn && (
                <Button color="primary" className="w-full mt-4 text-xs" onClick={navigateToResume}>
                  Completing your profile
                </Button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center h-full min-h-32">
          <Spinner />
        </div>
      )}
    </Card>
  );
};

export default UserInformation;
